import React from 'react'
import {
  Alert,
  Box,
  Chip,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'

export const buildPricingRules = (categories = [], currentRules = []) => {
  const byCategory = new Map(
    currentRules.map((rule) => [rule.category, rule])
  )

  return categories.map((category) => ({
    category,
    adjustment_type:
      byCategory.get(category)?.adjustment_type ?? 'percentage',
    adjustment_value:
      byCategory.get(category)?.adjustment_value ?? '',
  }))
}

export const pricingRulesComplete = (categories = [], rules = []) =>
  categories.length > 0 &&
  categories.every((category) => {
    const rule = rules.find((item) => item.category === category)
    return (
      rule &&
      ['percentage', 'fixed_amount'].includes(rule.adjustment_type) &&
      rule.adjustment_value !== '' &&
      rule.adjustment_value !== null &&
      Number(rule.adjustment_value) >= 0
    )
  })

const PricingEditor = ({
  categories = [],
  rules = [],
  onChange,
  loading = false,
  loadError = '',
}) => {
  const configuredCount = rules.filter(
    (rule) =>
      rule.adjustment_value !== '' &&
      rule.adjustment_value !== null &&
      Number(rule.adjustment_value) >= 0
  ).length

  const updateRule = (category, field, value) => {
    onChange(
      rules.map((rule) =>
        rule.category === category ? { ...rule, [field]: value } : rule
      )
    )
  }

  return (
    <Box
      component="fieldset"
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        m: 0,
        mt: 2,
        p: { xs: 2, md: 3 },
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box
        component="legend"
        sx={{ px: 1, fontSize: '1.1rem', fontWeight: 600 }}
      >
        Category Pricing
      </Box>

      <Box
        sx={{
          alignItems: { xs: 'flex-start', sm: 'center' },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography color="text.secondary" sx={{ minWidth: 0, fontSize: 14, lineHeight: 1.6 }}>
          Set the adjustment applied to the imported base price for every
          product category.
        </Typography>
        <Chip
          color={configuredCount === categories.length && categories.length > 0 ? 'success' : 'warning'}
          label={`${configuredCount} of ${categories.length} configured`}
          size="small"
          sx={{ flexShrink: 0 }}
          variant="outlined"
        />
      </Box>

      {loadError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading product categories…
        </Alert>
      )}
      {!loading && !loadError && categories.length === 0 && (
        <Alert severity="warning" role="alert">
          No product categories are available. Add categorized products before
          creating a customer.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        {rules.map((rule, index) => {
          const isPercentage = rule.adjustment_type === 'percentage'
          const helperId = `pricing-rule-${index}-help`

          return (
            <Box key={rule.category} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: { xs: 1.5, sm: 2 },
                  height: '100%',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
              >
                <Typography component="h3" fontWeight={600} sx={{ mb: 2, overflowWrap: 'anywhere' }}>
                  {rule.category}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <TextField
                      fullWidth
                      id={`pricing-rule-${index}-type`}
                      label="Adjustment type"
                      onChange={(event) =>
                        updateRule(
                          rule.category,
                          'adjustment_type',
                          event.target.value
                        )
                      }
                      required
                      select
                      value={rule.adjustment_type}
                    >
                      <MenuItem value="percentage">Percentage</MenuItem>
                      <MenuItem value="fixed_amount">Fixed amount</MenuItem>
                    </TextField>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <TextField
                      aria-describedby={helperId}
                      error={
                        rule.adjustment_value !== '' &&
                        Number(rule.adjustment_value) < 0
                      }
                      fullWidth
                      helperText={
                        rule.adjustment_value !== '' &&
                        Number(rule.adjustment_value) < 0
                          ? 'Enter zero or a positive value.'
                          : isPercentage
                            ? 'Added as a percentage of base price.'
                            : 'Added to each item’s base price.'
                      }
                      FormHelperTextProps={{ id: helperId }}
                      id={`pricing-rule-${index}-value`}
                      inputProps={{ min: 0, step: '0.001' }}
                      InputProps={{
                        startAdornment: !isPercentage ? (
                          <InputAdornment position="start">$</InputAdornment>
                        ) : undefined,
                        endAdornment: isPercentage ? (
                          <InputAdornment position="end">%</InputAdornment>
                        ) : undefined,
                      }}
                      label="Adjustment value"
                      onChange={(event) =>
                        updateRule(
                          rule.category,
                          'adjustment_value',
                          event.target.value
                        )
                      }
                      required
                      type="number"
                      value={rule.adjustment_value}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default PricingEditor
